<template>
  <v-card>
    <v-toolbar
      class="datatable-object settings-window-toolbar"
      extension-height="40"
      flat
      height="80"
    >
      <v-avatar
        :color="$t('Settings.Kds.Toolbar.Color')"
      >
        <v-icon dark>
          {{ $t('Settings.Kds.Toolbar.Icon') }}
        </v-icon>
      </v-avatar>

      <v-toolbar-title class="pl-3">
        <div class="body-3">
          {{ $tc('Settings.Printer.Title', 0) }}
        </div>

        <div class="text-caption">
          {{ $t('Settings.Kds.Toolbar.Subtitle') }}
        </div>
      </v-toolbar-title>

      <v-spacer />

      <template #extension>
        <v-tabs
          v-model="tab"
          background-color="transparent"
          grow
          height="40"
          slider-color="blue"
        >
          <v-tab key="groups">
            {{ $t('Settings.Kds.Tab.Groups') }}
          </v-tab>

          <v-tab key="preparation">
            {{ $t('Settings.Kds.Tab.Preparation') }}
          </v-tab>
        </v-tabs>
      </template>
    </v-toolbar>

    <v-divider />

    <v-card-text
      id="settingsWindow"
      :class="[{'pt-0': tab === 0}]"
      style="overflow-y: auto;"
    >
      <v-form
        ref="settingsForm"
        v-model="settingsFormValid"
        @submit.prevent
      >
        <v-tabs-items v-model="tab">
          <v-tab-item key="groups">
            <v-toolbar
              class="printers-group"
              color="transparent"
              flat
            >
              <v-btn
                class="elevation-0"
                color="primary"
                @click="onAddPrintersGroupClick"
              >
                <v-icon left>
                  add
                </v-icon>
                {{ $t('Common.Button.Add') }}
              </v-btn>
            </v-toolbar>

            <v-divider />

            <v-container
              class="pa-0"
              fluid
            >
              <v-row dense>
                <v-col cols="12">
                  <v-data-table
                    :footer-props="dataTable.footerProps"
                    :headers="printerGroupsHeaders"
                    :height="dataTable.resize.bodyHeight"
                    :items="dataTable.data"
                    :loading="dataTable.loading"
                    :loading-text="dataTable.text.loadingText"
                    :no-data-text="dataTable.text.noDataText"
                    :no-results-text="dataTable.text.noResultsText"
                    :options.sync="dataTable.options"
                    class="elevation-0"
                    fixed-header
                    hide-default-footer
                  >
                    <template #[`item.Id`]="{item}">
                      {{ item.Id }}
                    </template>

                    <template #[`item.Name`]="{item}">
                      <div :class="{'caption': $vuetify.breakpoint.xsOnly}">
                        {{ item.Name }}
                      </div>
                    </template>

                    <template #[`item.Description`]="{item}">
                      <div :class="{'caption': $vuetify.breakpoint.xsOnly}">
                        {{ item.Description }}
                      </div>
                    </template>

                    <template #[`item.UsersCount`]="{item}">
                      <v-chip
                        class="justify-center"
                        color="grey"
                        label
                        small
                        text-color="white"
                      >
                        {{ item.UsersCount || 0 }}
                      </v-chip>
                    </template>

                    <template #[`item.PrintersCount`]="{item}">
                      <v-chip
                        class="justify-center"
                        color="grey"
                        label
                        small
                        text-color="white"
                      >
                        {{ item.PrintersCount || 0 }}
                      </v-chip>
                    </template>

                    <template #[`item.Default`]="{item}">
                      <v-switch
                        v-model="item.Default"
                        :disabled="!userCanManage || !canSetPrintGroupDefault(item)"
                        :label="item.Default ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                        class="ma-0"
                        color="info"
                        hide-details
                        inset
                        @change="onTogglePrinterGroupDefault(item)"
                      />
                    </template>

                    <template #[`item.Status`]="{item}">
                      <v-switch
                        v-model="item.Status"
                        :disabled="!userCanManage"
                        :label="item.Status ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')"
                        class="ma-0"
                        color="success"
                        hide-details
                        inset
                        @change="onTogglePrinterGroupStatus(item)"
                      />
                    </template>

                    <template #[`item.Actions`]="{item}">
                      <v-btn
                        v-if="userCanDelete && userCanManage && parseInt(item.PrintersCount) === 0 && parseInt(item.UsersCount) === 0"
                        :class="$t('Settings.Permission.Toolbar.Button.Delete.Color')"
                        :disabled="parseInt(item.PrintersCount) > 0 || parseInt(item.UsersCount) > 0 || item.Loading"
                        :loading="item.loading"
                        class="white--text elevation-2 ma-1"
                        icon
                        small
                        @click="onDeletePrintersGroupClick(item)"
                      >
                        <v-icon small>
                          delete
                        </v-icon>
                      </v-btn>

                      <v-btn
                        v-if="userCanAdd && userCanManage"
                        class="cyan white--text elevation-2 ma-1"
                        icon
                        small
                        @click="onEditPrintersGroupClick(item)"
                      >
                        <v-icon small>
                          edit
                        </v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>

          <v-tab-item key="preparation">
            <v-container
              class="pa-0"
              fluid
            >
              <v-row dense>
                <v-col cols="12">
                  <template v-if="users.length">
                    <v-card flat>
                      <v-card-text
                        :class="isDarkMode ? '' : 'grey lighten-4'"
                        class="pa-0 ma-0 overflow-auto"
                      >
                        <div class="divTable">
                          <div class="divTableHeading">
                            <div class="divTableRow">
                              <div class="divTableHead">
                                {{ $t('Settings.Kds.Header.Name') }}
                              </div>
                              <div class="divTableHead">
                                {{ $t('Settings.Kds.Header.Group') }}
                              </div>
                              <div class="divTableHead">
                                {{ $t('Settings.Kds.Header.Active') }}
                              </div>
                            </div>
                          </div>
                          <div
                            v-for="(user, userIndex) in users"
                            :key="userIndex"
                            class="divTableRow"
                          >
                            <div class="divTableCell">
                              {{ user.Name }}
                            </div>

                            <div class="divTableCell">
                              <v-select
                                v-model="user.Groups"
                                :items="appPrinterGroups"
                                :label="$t('Settings.Kds.Header.Group')"
                                hide-details
                                item-text="Name"
                                item-value="Id"
                                multiple
                                required
                                solo
                                style="min-width: 85px; max-width: 480px;"
                              >
                                <template #selection="{item, index}">
                                  <v-chip
                                    v-if="index < 4"
                                    label
                                    small
                                  >
                                    <span>{{ item.Name }}</span>
                                  </v-chip>
                                  <span
                                    v-if="index > 3"
                                    class="grey--text text-caption"
                                  >(+{{ user.Groups.length - 4 }})</span>
                                </template>
                              </v-select>
                            </div>

                            <div class="divTableCell">
                              <v-switch
                                v-model="user.Active"
                                :disabled="!userCanManage"
                                :label="user.Active ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                color="success"
                                hide-details
                                inset
                              />
                            </div>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </template>

                  <template v-else>
                    <v-container
                      class="pa-0"
                      fluid
                    >
                      <v-row class="">
                        <v-col cols="12">
                          <div class="text-center grey--text">
                            <v-icon
                              class="mb-4"
                              color="grey lighten-2"
                              size="86"
                            >
                              mdi-monitor-dashboard
                            </v-icon>

                            <div
                              class="text-h6 mb-2"
                              style="height: auto;"
                            >
                              {{ $t('Settings.Kds.Notification.NoKds.Title') }}
                            </div>

                            <div
                              class="text-body-2"
                              style="height: auto;"
                            >
                              {{ $t('Settings.Kds.Notification.NoKds.Subtitle') }}
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-container>
                  </template>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>
        </v-tabs-items>
      </v-form>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4 settings-window-actions">
      <v-spacer />

      <v-btn
        class="light-blue--text text--darken-1"
        outlined
        @click="onCancelButtonClick"
      >
        {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
      </v-btn>

      <v-btn
        v-if="userCanManage && tab > 0"
        :disabled="viewData===null || isSaving"
        :loading="isSaving"
        class="green white--text elevation-0"
        @click="onSaveButtonClick"
      >
        {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
    </v-card-actions>

    <printer-group-add-dialog
      :can-set-default="canSetPrintGroupDefault(selectedPrinterGroup)"
      :item-to-edit="selectedPrinterGroup"
      :visible.sync="printerGroupAddDialogVisible"
    />
  </v-card>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import PrinterGroupAddDialog from '@/components/settings/printers/PrinterGroupAddDialog.vue'
import DataTable from '@/mixins/data-table/dataTable'
import EditMachineDialog from '@/components/common/EditMachineDialog.vue'
import {clone} from '@/lib/helper/helper.js'

export default {
  components: {EditMachineDialog, PrinterGroupAddDialog},
  mixins    : [AppData, Auth, DataTable],
  data() {
    return {
      tab                         : null,
      isSaving                    : false,
      settingsFormValid           : false,
      printerGroupAddDialogVisible: false,
      selectedPrinterGroup        : null,
      dataTable                   : {
        socketCommand: {
          list: window.SocketCommand.System.Kds.Get
        },
        resize: {
          offset: 185
        },
        options: {
          serverSideEnabled: false,
          itemsPerPage     : -1
        }
      }
    }
  },
  computed: {
    users() {
      return this.viewData?.users || []
    },

    printerGroups() {
      return this.viewData?.groups || []
    },

    printerGroupsHeaders() {
      return [
        {
          text    : '#',
          align   : 'left',
          value   : 'Id',
          sortable: false
        },
        {
          text    : this.$t('Common.Misc.Name'),
          align   : 'left',
          value   : 'Name',
          sortable: false
        },
        {
          text    : this.$t('Common.Misc.Desc'),
          align   : 'left',
          value   : 'Description',
          sortable: false
        },
        {
          text    : this.$tc('Settings.Kds.Title', 0),
          align   : 'center',
          value   : 'UsersCount',
          sortable: false
        },
        {
          text    : this.$tc('Common.Misc.Printer', 0),
          align   : 'center',
          value   : 'PrintersCount',
          sortable: false
        },
        {
          text    : this.$t('Common.Misc.Default'),
          align   : 'left',
          value   : 'Default',
          sortable: false
        },
        {
          text    : this.$t('Common.Misc.Status'),
          align   : 'left',
          value   : 'Status',
          sortable: false
        },
        {
          text    : '',
          align   : 'right',
          value   : 'Actions',
          sortable: false
        }
      ]
    }
  },
  watch: {},
  created() {
    this.setData({
      groups  : this.appPrinterGroups || [],
      printers: []
    })
  },
  mounted() {
    this.$bus.$on(window.SocketCommand.System.Kds.Save, this.onSaveResult)
    this.$bus.$on(window.SocketCommand.Settings.Printer.Group.Save, this.onSavePrinterGroupResult)
    this.$bus.$on(window.SocketCommand.Settings.Printer.Group.Delete, this.onDeletePrinterGroupResult)
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.System.Kds.Save, this.onSaveResult)
    this.$bus.$off(window.SocketCommand.Settings.Printer.Group.Save, this.onSavePrinterGroupResult)
    this.$bus.$off(window.SocketCommand.Settings.Printer.Group.Delete, this.onDeletePrinterGroupResult)
  },
  methods: {
    canSetPrintGroupDefault(item) {
      return item?.Default || this.dataTable.data.every(p => !p.Default)
    },

    onAddPrintersGroupClick() {
      this.selectedPrinterGroup = null
      this.printerGroupAddDialogVisible = true
    },
    onSavePrinterGroupResult() {
      this.selectedPrinterGroup = null
      this.getData()
    },

    onEditPrintersGroupClick(item) {
      if (!item) return
      const payload = clone(item)
      this.selectedPrinterGroup = payload
      this.printerGroupAddDialogVisible = true
    },

    onTogglePrinterGroupDefault(item) {
      if (!item) return
      const payload = clone(item)
      window.callAS(window.SocketCommand.Settings.Printer.Group.Save, payload)
    },

    onTogglePrinterGroupStatus(item) {
      if (!item) return
      const payload = clone(item)
      window.callAS(window.SocketCommand.Settings.Printer.Group.Save, payload)
    },

    onDeletePrintersGroupClick(item) {
      if (!item) return
      this.$set(item, 'loading', true)
      const payload = {Id: item.Id}
      window.callAS(window.SocketCommand.Settings.Printer.Group.Delete, payload)
    },
    onDeletePrinterGroupResult() {
      this.getData()
    },

    setData(data) {
      this.$set(this.viewData, 'items', data?.groups || [])
      this.$set(this.viewData, 'groups', data?.groups || [])
      this.$set(this.viewData, 'users', data?.users || [])

      this.$nextTick(() => {
        this.setDataInternal(this.viewData)
      })
    },

    onCancelButtonClick() {
      this.$router.replace({name: 'SystemSettings'})
    },

    onSaveButtonClick() {
      this.isSaving = true
      window.callAS(window.SocketCommand.System.Kds.Save, {users: this.viewData.users})
    },

    onSaveResult(data) {
      this.isSaving = false

      if (data.status) {
        this.getData()

        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.Kds.Notification.Success'),
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.Kds.Notification.Error'),
          type: 'error',
          icon: 'warning'
        })
      }
    }
  }
}
</script>

<style scoped>
:deep(.printers-group .v-toolbar__content) {
  padding: 0;
}

:deep(.v-input--selection-controls.v-input--switch) {
  display: inline-block;
  position: relative !important;
  width: 75px !important;
}

:deep(.v-input--selection-controls.v-input--switch label) {
  font-size: 12px !important;
  padding-right: 0;
  margin: 0;
}
</style>
